<template>
  <div class="my-training-container">
    <v-skeleton-loader v-if="loading" height="100" type="image" class="rounded-xl custom-skeleton"></v-skeleton-loader>

    <div v-else class="calendar-card glass-card pa-3 rounded-xl">
      <div class="calendar-heading mb-3 px-1">
        <div><span>DEINE AKTIVITÄTEN</span><strong>Training & Kurse</strong></div>
        <v-btn icon color="white" aria-label="Kalender öffnen" @click="openDatePicker" style="background: rgba(255, 255, 255, 0.06)">
          <v-icon small>mdi-calendar-month</v-icon>
        </v-btn>
      </div>
      <v-progress-linear
        v-if="trainingProgress > 0"
        :value="trainingProgress"
        height="6"
        rounded
        color="primary"
        background-color="rgba(255,255,255,0.1)"
        class="mb-4"></v-progress-linear>

      <div class="days-row d-flex justify-space-between">
        <div
          v-for="(day, index) in calendar"
          :key="index"
          class="day-item"
          :class="{ 'day-active': hasActivity(day), 'day-today': day.today, 'day-selected': activeIndex === index }"
          @click="selectDay(day, index)">
          <span class="day-name caption">{{ day.short }}</span>
          <div class="day-circle">
            <v-icon v-if="hasActivity(day) && activeIndex !== index" x-small color="white">mdi-check-bold</v-icon>
            <span v-else class="day-date">{{ $moment(day.ts * 1000).format("D") }}</span>
          </div>
        </div>
      </div>

      <button v-if="nextCourse" type="button" class="next-course" @click="openCourseDay(nextCourse)">
        <span class="next-course-icon"><v-icon color="white" size="20">mdi-account-group</v-icon></span>
        <span class="next-course-copy">
          <small>NÄCHSTER KURS · ANGEMELDET</small>
          <strong>{{ nextCourse.title }}</strong>
          <span>{{ courseDateLabel(nextCourse.startAt) }}<template v-if="nextCourse.studioName"> · {{ nextCourse.studioName }}</template></span>
        </span>
        <v-icon color="white" size="20">mdi-chevron-right</v-icon>
      </button>

      <v-expand-transition>
        <div v-if="selectedDayData && !opensCustomerHub" class="day-detail mt-3 pa-3 rounded-lg glass-inner-card">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="font-weight-bold white--text text-uppercase d-flex align-center">
              <v-icon small color="white" class="mr-2">mdi-calendar-range</v-icon>
              {{ $moment(selectedDayData.date).format("dd, DD. MMMM") }}
            </span>
            <v-btn x-small icon @click="closeDetails">
              <v-icon small color="grey">mdi-close</v-icon>
            </v-btn>
          </div>

          <div v-if="loadingDayDetails" class="text-center pa-4">
            <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
          </div>

          <v-alert v-else-if="activityMode && dayLoadError" type="warning" text dense>{{ dayLoadError }}</v-alert>
          <div v-else>
            <div
              v-if="!hasAnythingToDo(selectedDayData)"
              class="recharge-card relative d-flex flex-column align-center justify-center py-6 px-4 rounded-xl mt-2 overflow-hidden">
              <div v-if="!isFutureDay" class="ambient-glow"></div>

              <div class="icon-wrapper mb-3">
                <div v-if="!isFutureDay" class="pulse-ring"></div>
                <v-icon x-large color="#00963f" class="z-index-2">{{ isFutureDay ? "mdi-calendar-blank-outline" : "mdi-flash" }}</v-icon>
              </div>

              <h3 class="white--text text-h6 font-weight-black text-uppercase tracking-wider z-index-2 mb-1">{{ isFutureDay ? "Noch nichts geplant" : "Recovery Mode" }}</h3>

              <p class="caption grey--text text--lighten-1 mb-3 z-index-2 text-center" style="max-width: 80%">
                <template v-if="isFutureDay">Für diesen Tag sind noch keine Kurse oder Aktivitäten eingetragen.</template>
                <template v-else>Muskeln wachsen in der Ruhephase. <br />Entspann dich, Champ.</template>
              </p>

              <div v-if="!isFutureDay" class="status-pill d-flex align-center px-3 py-1 rounded-pill z-index-2">
                <v-icon x-small color="#00963f" class="mr-2 blink">mdi-heart-pulse</v-icon>
                <span class="caption font-weight-bold" style="color: #00963f; letter-spacing: 1px">REGENERATION AKTIV</span>
              </div>
            </div>

            <div v-if="selectedDayData.checkin" class="activity-row d-flex align-center mb-2 pa-3 rounded-lg success-gradient-soft">
              <v-avatar size="32" color="rgba(0,0,0,0.2)" class="mr-3">
                <v-icon small color="white">mdi-map-marker-check</v-icon>
              </v-avatar>
              <div class="d-flex flex-column">
                <span class="caption font-weight-bold white--text">Studio Check-in</span>
                <span class="caption white--text opacity-80">
                  {{ $moment(selectedDayData.checkin).format("HH:mm") }}
                  <span v-if="selectedDayData.checkout">- {{ $moment(selectedDayData.checkout).format("HH:mm") }}</span>
                </span>
              </div>
            </div>

            <div
              v-for="(activity, i) in combinedActivities"
              :key="activity.id || i"
              class="activity-card-shell mb-2 rounded-lg overflow-hidden"
              :style="activityStyle(activity)">
              <div class="activity-row d-flex align-center pa-3">
                <v-avatar size="36" :color="activity.isGroupFitness || activity.isWatch ? 'rgba(0,150,63,0.18)' : 'rgba(255,255,255,0.08)'" class="mr-3">
                  <v-icon x-small color="white">{{ activity.icon }}</v-icon>
                </v-avatar>
                <div class="d-flex flex-column flex-grow-1 min-width-0">
                  <span class="activity-title">{{ activity.title }}</span>
                  <span class="activity-subtitle">{{ activity.type }}<template v-if="activityTime(activity)"> · {{ activityTime(activity) }}</template></span>
                  <span v-if="activity.isGroupFitness" class="activity-status">{{ courseStatusLabel(activity.status) }}</span>
                </div>
                <span v-if="activity.isWatch" class="watch-source-pill ml-2">
                  <v-icon small color="white">mdi-watch-variant</v-icon>
                  <span class="sr-only">{{ activity.watchDeviceLabel }} aufgezeichnet</span>
                </span>
              </div>

              <div v-if="activity.watchHealth" class="watch-home-health px-3 pb-3">
                <div class="watch-home-metrics">
                  <div class="watch-home-metric">
                    <span>Ø PULS</span>
                    <strong>{{ activity.watchHealth.averageHeartRate }} <small>BPM</small></strong>
                  </div>
                  <div class="watch-home-metric">
                    <span>MAX</span>
                    <strong>{{ activity.watchHealth.maximumHeartRate }} <small>BPM</small></strong>
                  </div>
                  <div class="watch-home-metric">
                    <span>AKTIVE ENERGIE</span>
                    <strong>{{ activity.watchHealth.calories }} <small>kcal</small></strong>
                  </div>
                </div>

                <button
                  v-if="activity.watchHealth.samples.length > 1"
                  type="button"
                  class="watch-home-chart watch-home-chart-button mt-2"
                  aria-label="Pulsverlauf im Vollbild öffnen"
                  @click.stop="openHeartRateFullscreen(activity)">
                  <svg viewBox="0 0 260 54" preserveAspectRatio="none" role="img" aria-label="Pulsverlauf">
                    <defs>
                      <linearGradient :id="'watch-fill-' + i" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="#ff536d" stop-opacity="0.34" />
                        <stop offset="100%" stop-color="#ff536d" stop-opacity="0" />
                      </linearGradient>
                    </defs>
                    <polygon :points="watchSparklineArea(activity)" :fill="'url(#watch-fill-' + i + ')'" />
                    <polyline :points="watchSparklinePoints(activity)" fill="none" stroke="#ff6178" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <span class="watch-home-expand-pill"><v-icon x-small color="white">mdi-arrow-expand-all</v-icon> Vollbild</span>
                </button>

                <div class="watch-home-health-footer mt-2">
                  <span><v-icon small color="white">mdi-timer-outline</v-icon> {{ activity.watchHealth.duration }}</span>
                  <span class="watch-home-device">{{ activity.watchDeviceLabel }}</span>
                  <button type="button" class="watch-home-detail-button" @click.stop="openWatchDetail(activity)">
                    Analyse <v-icon x-small color="white">mdi-chevron-right</v-icon>
                  </button>
                </div>
                <p v-if="activity.watchHealth.missingMeasurements" class="watch-home-note">Für diese Aufzeichnung wurden noch keine Puls- oder Kalorienwerte übertragen.</p>
              </div>
            </div>

            <div v-if="userService.hasRight('gf_cp_instructor')" class="mt-3 pt-2">
              <v-btn block small color="primary" class="font-weight-bold" @click="showGFHours = !showGFHours">
                <v-icon left x-small>mdi-invoice-clock-outline</v-icon> {{ showGFHours ? "schliessen" : "Abrechnung" }}
              </v-btn>
              <v-expand-transition>
                <div v-if="showGFHours" class="mt-2 glass-inner-card pa-2 rounded">
                  <div v-if="fetchingGFHours" class="text-center">
                    <v-progress-circular size="20" indeterminate color="primary"></v-progress-circular>
                  </div>
                  <div v-else>
                    <div v-if="!GFHourInfos || !GFHourInfos.list.length" class="caption grey--text text-center">Keine Stunden gefunden.</div>
                    <div v-else>
                      <div class="caption white--text mb-1 font-weight-bold">Gesamt: {{ GFHourInfos.totalDuration }} Min</div>
                      <div
                        v-for="(c, idx) in GFHourInfos.list"
                        :key="'gf' + idx"
                        class="caption grey--text d-flex justify-space-between border-bottom-light py-1">
                        <span>{{ c.courseName }}</span
                        ><span>{{ c.courseDate }} ({{ c.duration }}m) </span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </div>
      </v-expand-transition>
    </div>
    <v-dialog v-model="datePickerDialog" max-width="400px" overlay-opacity="0.8" overlay-color="#000" content-class="glass-dialog-wrapper">
      <div class="glass-card pa-5 rounded-xl overflow-hidden relative">
        <div class="ambient-glow dialog-glow"></div>

        <div class="d-flex justify-space-between align-center mb-4 relative" style="z-index: 2">
          <div>
            <div class="caption primary--text text-uppercase font-weight-black tracking-wider">Dein Kalender</div>
            <div class="text-h5 white--text font-weight-bold">Wähle ein Datum</div>
          </div>
          <v-btn icon color="white" @click="datePickerDialog = false" style="background: rgba(255, 255, 255, 0.1)">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-progress-linear v-if="loadingEvents" indeterminate color="primary" height="2" class="mb-2" style="z-index: 2"></v-progress-linear>
        <v-date-picker
          v-model="activeDate"
          :events="calendarEvents"
          color="primary"
          locale="de-CH"
          full-width
          no-title
          :show-current="false"
          class="glass-picker custom-events"
          :allowed-dates="(date) => activityMode || date <= $moment().add(7, 'days').format('YYYY-MM-DD')"
          @change="onDateSelected"></v-date-picker>
      </div>
    </v-dialog>

    <watch-workout-detail
      v-if="canUseWatchFeature"
      v-model="watchDetailOpen"
      :activity="watchDetailActivity"
      @open-pulse="openHeartRateFullscreen" />

    <watch-heart-rate-fullscreen
      v-if="canUseWatchFeature"
      v-model="heartRateFullscreenOpen"
      :samples="heartRateFullscreenHealth.timelineSamples || []"
      :intervals="heartRateFullscreenHealth.intervals || []"
      :markers="heartRateFullscreenHealth.markers || []"
      :title="heartRateFullscreenActivity ? heartRateFullscreenActivity.title : 'Pulsverlauf'"
      :subtitle="heartRateFullscreenSubtitle"
      :source-name="heartRateFullscreenDeviceLabel"
      :health-source-name="heartRateFullscreenHealthSourceLabel"
      :duration-seconds="heartRateFullscreenHealth.durationSeconds || 0"
      :average="heartRateFullscreenHealth.averageHeartRate"
      :minimum="heartRateFullscreenHealth.minimumHeartRate"
      :maximum="heartRateFullscreenHealth.maximumHeartRate" />
  </div>
</template>
<script>
import api from "../../api"
import { compactCalendar, readOverview } from "@/store/homeOverviewStore"
import userService from "../../services/userService"
import trainingFeatureAccess from "@/services/trainingFeatureAccess"
import customerHubPreferences from "@/services/customerHubPreferences"
import customerHubApi from "@/api/customerHubApi"
import { customerHubActivityLocation, customerHubDate } from "@/services/customerHubNavigation"
import WatchHeartRateFullscreen from "./WatchHeartRateFullscreen.vue"
import WatchWorkoutDetail from "./WatchWorkoutDetail.vue"
import { calendarActivities } from "@/services/trainingCalendarActivities"

export default {
  name: "myTraining",
  components: { WatchHeartRateFullscreen, WatchWorkoutDetail },
  props: {
    activityMode: { type: Boolean, default: false },
    selectedDate: { type: String, default: "" },
  },
  data() {
    return {
      userService,
      loading: true,
      calendar: [],
      trainingProgress: 0,

      // Selection State
      activeIndex: null,
      selectedDayData: null,
      loadingDayDetails: false,
      dayLoadError: "",

      // Instructor State
      showGFHours: false,
      fetchingGFHours: false,
      GFHourInfos: null,

      // NEU: Calendar Picker State
      datePickerDialog: false,
      events: {}, // Datum -> Farben für Anwesenheit, Groupfitness und Training
      activeDate: new Date().toISOString().substr(0, 10),

      // NEU HINZUGEFÜGT:
      eventsLoaded: false,
      loadingEvents: false,
      heartRateFullscreenOpen: false,
      heartRateFullscreenActivity: null,
      watchDetailOpen: false,
      watchDetailActivity: null,
      upcomingCourses: [],
      dayRequestVersion: 0,
      upcomingRequestVersion: 0,
      calendarRequestVersion: 0,
    }
  },
  computed: {
    customerHubEnabled() { return customerHubPreferences.isEnabled() },
    opensCustomerHub() { return this.customerHubEnabled && !this.activityMode },
    canUseWatchFeature() {
      return trainingFeatureAccess.isAllowed()
    },
    combinedActivities() {
      return calendarActivities(this.selectedDayData, {
        showWatchDetails: this.canUseWatchFeature,
        normalizeHealth: (workout) => this.normalizeWatchHealth(workout),
      })
    },
    isFutureDay() {
      return Boolean(this.selectedDayData?.date && this.$moment(this.selectedDayData.date).isAfter(this.$moment(), "day"))
    },
    nextCourse() {
      return this.upcomingCourses[0] || null
    },
    calendarEvents() {
      const events = { ...this.events }
      this.upcomingCourses.forEach((course) => {
        const date = this.$moment(course.startAt).format("YYYY-MM-DD")
        if (!events[date]?.length) events[date] = ["primary"]
      })
      return events
    },
    heartRateFullscreenHealth() {
      return this.heartRateFullscreenActivity?.watchHealth || {}
    },
    heartRateFullscreenSubtitle() {
      if (!this.heartRateFullscreenActivity) return ""
      const activityType = this.heartRateFullscreenActivity.isGroupFitness
        ? "Groupfitness"
        : `${this.heartRateFullscreenActivity.watchDeviceLabel} Training`
      const date = this.selectedDayData?.date ? this.$moment(this.selectedDayData.date).format("dd, DD. MMMM YYYY") : ""
      return [activityType, date].filter(Boolean).join(" · ")
    },
    heartRateFullscreenDeviceLabel() {
      return this.heartRateFullscreenActivity?.watchDeviceLabel || "Watch"
    },
    heartRateFullscreenHealthSourceLabel() {
      return this.heartRateFullscreenActivity?.watchHealthSource || "Gesundheitsdaten"
    },
  },
  watch: {
    "$store.state.studioStore.selectedStudio._id": "refreshCalendar",
    "$store.state.userStore.user._id": "refreshCalendar",
    canUseWatchFeature: "refreshCalendar",
    customerHubEnabled: "refreshCalendar",
    selectedDate(value) {
      const date = customerHubDate(value)
      if (this.activityMode && date && this.$moment(this.selectedDayData?.date).format("YYYY-MM-DD") !== date) this.onDateSelected(date)
    },
  },
  async created() {
    this._calendarAlive = true
    this.restoreOverview()
    this._onWatchSessionSaved = (event) => {
      if (String(event?.detail?.userId || "") === String(userService.getUserId() || "")) this.scheduleCalendarRefresh()
    }
    this._onCalendarVisible = () => {
      if (document.visibilityState === "visible") this.scheduleCalendarRefresh()
    }
    window.addEventListener("roody:watch-session-saved", this._onWatchSessionSaved)
    document.addEventListener("visibilitychange", this._onCalendarVisible)
    await Promise.all([this.createCalendar(), this.fetchUpcomingCourses()])
    if (this._calendarAlive && this.activityMode) {
      await this.onDateSelected(customerHubDate(this.selectedDate) || this.$moment().format("YYYY-MM-DD"))
    }
  },
  beforeDestroy() {
    this._calendarAlive = false
    this.dayRequestVersion++
    this.upcomingRequestVersion++
    this.calendarRequestVersion++
    clearTimeout(this._calendarRefreshTimer)
    window.removeEventListener("roody:watch-session-saved", this._onWatchSessionSaved)
    document.removeEventListener("visibilitychange", this._onCalendarVisible)
  },
  methods: {
    overviewScope() {
      const owner = userService.getUserId()
      const studio = this.$store.state.studioStore.selectedStudio?._id
      return owner && studio ? JSON.stringify([owner, studio, this.$moment().format("YYYY-MM-DD"), this.canUseWatchFeature, this.customerHubEnabled]) : null
    },
    restoreOverview() {
      this._overviewScope = this.overviewScope()
      const state = this.$store.state.homeOverviewStore
      const calendar = readOverview(state, "calendar", this._overviewScope)
      this.applyCalendar(calendar || [])
      this.upcomingCourses = this.filterUpcoming(readOverview(state, "upcoming", this._overviewScope) || [])
      this.loading = calendar === null
    },
    applyCalendar(calendar) {
      this.calendar = compactCalendar(calendar)
      this.trainingProgress = Math.round((100 / 7) * this.calendar.filter((day) => this.hasActivity(day)).length)
    },
    filterUpcoming(courses) {
      return courses.filter((course) => {
        const start = new Date(course.startAt).getTime()
        return Number.isFinite(start) && start >= Date.now() && (this.customerHubEnabled || start <= this.$moment().add(7, "days").endOf("day").valueOf())
      }).sort((left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime())
    },
    scheduleCalendarRefresh() {
      clearTimeout(this._calendarRefreshTimer)
      this._calendarRefreshTimer = setTimeout(() => this.refreshCalendar(), 250)
    },
    async refreshCalendar() {
      if (!this._calendarAlive) return
      if (this._overviewScope !== this.overviewScope()) {
        this.closeDetails()
        this.events = {}
        this.restoreOverview()
      }
      this.eventsLoaded = false
      const tasks = [this.createCalendar(), this.fetchUpcomingCourses()]
      if (this.selectedDayData?.date) {
        const date = this.selectedDayData.date
        tasks.push(this.loadDayData(this.$moment(date).format("YYYY-MM-DD"), date))
      }
      if (this.datePickerDialog) tasks.push(this.fetchExtraData())
      await Promise.all(tasks)
    },
    async fetchUpcomingCourses() {
      const version = ++this.upcomingRequestVersion
      const owner = userService.getUserId()
      const scope = this.overviewScope()
      const token = localStorage.getItem("token")
      const pilot = this.customerHubEnabled
      const current = () => this._calendarAlive && version === this.upcomingRequestVersion && owner === userService.getUserId() && scope === this.overviewScope() && token === localStorage.getItem("token")
      if ((!this.canUseWatchFeature && !pilot) || !owner) {
        this.upcomingCourses = []
        return
      }
      try {
        const response = pilot ? await customerHubApi.getUpcomingCourses(token) : await api.workoutApi.fetchWatchGroupFitnessActivities()
        if (!current()) return
        if (pilot && (response?.success !== true || String(response.userId) !== String(owner))) throw new Error("Invalid course response")
        this.upcomingCourses = this.filterUpcoming(Array.isArray(response?.activities) ? response.activities : [])
          .map(({ title, startAt, studioName }) => ({ title, startAt, studioName }))
        this.$store.commit("homeOverviewStore/save", { key: "upcoming", scope, data: this.upcomingCourses })
      } catch (error) {
        if (!current()) return
        const unauthorized = [401, 403].includes(error?.response?.status)
        if (pilot || unauthorized) {
          this.upcomingCourses = []
          this.$store.commit("homeOverviewStore/clear", { key: "upcoming", scope })
          if (pilot && unauthorized) customerHubPreferences.resolve(owner, token, [])
        }
      }
    },
    openCourseDay(course) {
      return this.onDateSelected(this.$moment(course.startAt).format("YYYY-MM-DD"))
    },
    courseDateLabel(value) {
      const date = this.$moment(value)
      return date.isSame(this.$moment(), "day") ? `Heute · ${date.format("HH:mm")}` : date.format("dd, DD. MMM · HH:mm")
    },
    activityTime(activity) {
      if (activity.startAt && this.$moment(activity.startAt).isValid()) return this.$moment(activity.startAt).format("HH:mm")
      return String(activity.time || "").replace(/\s*Uhr$/, "")
    },
    courseStatusLabel(status) {
      return { registered: "Angemeldet", visited: "Teilgenommen", instructor: "Als Kursleitung", recorded: "Aufgezeichnet" }[status] || "Groupfitness"
    },
    async createCalendar() {
      const version = ++this.calendarRequestVersion
      const scope = this.overviewScope()
      const token = localStorage.getItem("token")
      const current = () => this._calendarAlive && version === this.calendarRequestVersion && scope === this.overviewScope() && token === localStorage.getItem("token")
      try {
        const data = await api.fetchTrainingCalendar()
        if (!current()) return
        this.applyCalendar(data.calendar)
        this.$store.commit("homeOverviewStore/save", { key: "calendar", scope, data: this.calendar })
      } catch (e) {
        if (!current()) return
        if ([401, 403].includes(e.response?.status)) {
          this.applyCalendar([])
          this.$store.commit("homeOverviewStore/clear", { key: "calendar", scope })
        }
        console.error("Calendar fetch error", e)
      } finally {
        if (current()) this.loading = false
      }
    },

    // NEU: Events (Punkte) für den Monatskalender laden
    async fetchExtraData() {
      if (!this._calendarAlive || this.loadingEvents) return
      const owner = userService.getUserId()
      this.loadingEvents = true
      this.eventsLoaded = false
      try {
        const data = await api.fetchCompleteTrainingCalendar()
        if (!this._calendarAlive || owner !== userService.getUserId()) return
        if (!data?.events || typeof data.events !== "object" || Array.isArray(data.events)) throw new Error("Invalid calendar events")
        this.events = data.events
        this.eventsLoaded = true
      } catch (e) {
        console.error("Events fetch error", e)
      } finally {
        this.loadingEvents = false
      }
    },

    hasActivity(day) {
      return day && (day.hasActivity || day.foundWorkouts?.length || day.courseArray?.length || day.courses?.length || day.checkins?.length)
    },

    hasAnythingToDo(day) {
      return day && (day.checkin || day.workouts?.length || day.courseArray?.length)
    },

    activityStyle(activity) {
      return {
        background: activity.isWatch
          ? "linear-gradient(135deg, rgba(0, 150, 63, 0.1), rgba(255, 255, 255, 0.035))"
          : "rgba(255, 255, 255, 0.05)",
        borderLeft: "3px solid var(--v-primary-base, #00963f)",
      }
    },

    normalizeWatchHealth(workout) {
      const health = workout?.watchData?.health || {}
      const samples = (Array.isArray(health.heartRateSamples) ? health.heartRateSamples : [])
        .map((sample) => ({ x: Number(sample?.offsetSeconds), y: Number(sample?.bpm) }))
        .filter((sample) => Number.isFinite(sample.x) && sample.x >= 0 && Number.isFinite(sample.y) && sample.y > 0)
        .sort((left, right) => left.x - right.x)
      const sampleStep = Math.max(1, Math.ceil(samples.length / 90))
      const compactSamples = samples.filter((sample, index) => index % sampleStep === 0 || index === samples.length - 1)
      const pulseValues = samples.map((sample) => sample.y)
      const finiteOrNull = (value) => {
        const number = Number(value)
        return Number.isFinite(number) && number > 0 ? number : null
      }
      const averageFromSamples = pulseValues.length
        ? pulseValues.reduce((total, value) => total + value, 0) / pulseValues.length
        : null
      const average = finiteOrNull(health.averageHeartRate) || averageFromSamples
      const minimum = finiteOrNull(health.minimumHeartRate) || (pulseValues.length ? Math.min(...pulseValues) : null)
      const maximum = finiteOrNull(health.maximumHeartRate) || (pulseValues.length ? Math.max(...pulseValues) : null)
      const hasEnergyField = Object.prototype.hasOwnProperty.call(health, "activeEnergyKilocalories")
      const energy = hasEnergyField ? health.activeEnergyKilocalories : finiteOrNull(workout?.caloriesBurned)
      const calories = energy !== null && energy !== undefined && Number.isFinite(Number(energy)) && Number(energy) >= 0
        ? Number(energy) : null
      const durationSeconds = finiteOrNull(health.durationSeconds) || finiteOrNull(workout?.duration) || 0
      const watchData = workout?.watchData || {}
      const sessionStartedAt = new Date(watchData.startedAt || 0).getTime()
      const hasValidSessionStart = Number.isFinite(sessionStartedAt) && sessionStartedAt > 0
      const exercises = Array.isArray(watchData.exercises) ? watchData.exercises : []
      const titleByInstance = new Map(
        exercises.map((exercise) => [String(exercise.instanceId || exercise.exerciseId), exercise.title || "Übung"]),
      )
      const intervals = (hasValidSessionStart && Array.isArray(watchData.exerciseIntervals) ? watchData.exerciseIntervals : [])
        .map((interval, index) => ({
          key: interval.id || `exercise-${index}`,
          title: titleByInstance.get(String(interval.instanceId || interval.exerciseId)) || "Übung",
          startOffset: (new Date(interval.startedAt).getTime() - sessionStartedAt) / 1000,
          endOffset: (new Date(interval.endedAt).getTime() - sessionStartedAt) / 1000,
        }))
        .filter((interval) => Number.isFinite(interval.startOffset) && Number.isFinite(interval.endOffset) && interval.endOffset > interval.startOffset)
      const markers = (hasValidSessionStart ? exercises : []).flatMap((exercise, exerciseIndex) =>
        (Array.isArray(exercise.sets) ? exercise.sets : []).map((set, setIndex) => ({
          key: `${exercise.instanceId || exerciseIndex}-set-${set.position || setIndex + 1}`,
          x: (new Date(set.completedAt).getTime() - sessionStartedAt) / 1000,
          y: Number(set.heartRateAtRestEnd) || null,
          title: `${exercise.title || "Übung"} · Satz ${set.position || setIndex + 1}`,
          kind: "set",
        })),
      ).filter((marker) => Number.isFinite(marker.x) && marker.x >= 0)

      return {
        averageHeartRate: average ? Math.round(average) : "–",
        minimumHeartRate: minimum ? Math.round(minimum) : "–",
        maximumHeartRate: maximum ? Math.round(maximum) : "–",
        calories: calories === null ? "–" : Math.round(calories),
        missingMeasurements: !average && calories === null,
        duration: this.formatWatchDuration(durationSeconds),
        durationSeconds,
        samples: compactSamples,
        timelineSamples: samples,
        configuredMaximumHeartRate: finiteOrNull(health.configuredMaximumHeartRate),
        heartRateZones: Array.isArray(health.heartRateZones) ? health.heartRateZones : [],
        recovery: health.recovery && typeof health.recovery === "object" ? health.recovery : null,
        intervals,
        markers,
      }
    },

    openHeartRateFullscreen(activity) {
      if (!this.canUseWatchFeature) return
      if (!activity?.watchHealth?.timelineSamples?.length) return
      this.heartRateFullscreenActivity = activity
      this.heartRateFullscreenOpen = true
    },

    openWatchDetail(activity) {
      if (!this.canUseWatchFeature) return
      if (!activity?.isWatch) return
      this.watchDetailActivity = activity
      this.watchDetailOpen = true
    },

    formatWatchDuration(durationSeconds) {
      const totalSeconds = Math.max(0, Math.round(Number(durationSeconds) || 0))
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")} h`
      if (totalSeconds > 0 && minutes === 0) return "< 1 min"
      return `${minutes} min`
    },

    watchSparklinePoints(activity) {
      const samples = activity?.watchHealth?.samples || []
      if (samples.length < 2) return ""
      const minX = samples[0].x
      const maxX = samples[samples.length - 1].x
      const values = samples.map((sample) => sample.y)
      const rawMinY = Math.min(...values)
      const rawMaxY = Math.max(...values)
      const minY = rawMinY - 5
      const maxY = rawMaxY + 5
      const width = 260
      const height = 46
      const xRange = Math.max(1, maxX - minX)
      const yRange = Math.max(1, maxY - minY)
      return samples
        .map((sample) => {
          const x = ((sample.x - minX) / xRange) * width
          const y = 4 + (1 - (sample.y - minY) / yRange) * height
          return `${x.toFixed(1)},${y.toFixed(1)}`
        })
        .join(" ")
    },

    watchSparklineArea(activity) {
      const line = this.watchSparklinePoints(activity)
      if (!line) return ""
      return `0,54 ${line} 260,54`
    },

    closeDetails() {
      this.dayRequestVersion++
      this.loadingDayDetails = false
      this.selectedDayData = null
      this.activeIndex = null
      this.showGFHours = false
    },

    async selectDay(day, index) {
      if (this.opensCustomerHub) return this.$router.push(customerHubActivityLocation(this.$moment(day.ts * 1000).format("YYYY-MM-DD")))
      if (this.activityMode) return this.onDateSelected(this.$moment(day.ts * 1000).format("YYYY-MM-DD"))
      if (this.activeIndex === index) {
        this.closeDetails()
        return
      }

      this.activeIndex = index
      this.loadingDayDetails = true
      this.selectedDayData = { date: day.ts * 1000 }
      this.showGFHours = false

      await this.loadDayData(this.$moment(day.ts * 1000).format("YYYY-MM-DD"), day.ts * 1000)
    },

    // NEU: Date Picker öffnen
    async openDatePicker() {
      this.activeDate = this.selectedDayData ? this.$moment(this.selectedDayData.date).format("YYYY-MM-DD") : new Date().toISOString().substr(0, 10)
      this.datePickerDialog = true

      // NEU: Events lazy laden, wenn der Kalender geöffnet wird
      if (!this.eventsLoaded) {
        await this.fetchExtraData()
      }
    },

    // NEU: Wenn ein Datum im Picker gewählt wurde
    async onDateSelected(date) {
      if (!customerHubDate(date)) return
      this.datePickerDialog = false
      if (this.opensCustomerHub) return this.$router.push(customerHubActivityLocation(date))
      if (this.activityMode && date !== this.selectedDate) this.$emit("date-change", date)
      this.loadingDayDetails = true
      this.showGFHours = false

      // Prüfen, ob das gewählte Datum in der aktuellen Wochenansicht ist
      const indexInCalendar = this.calendar.findIndex((d) => this.$moment(d.ts * 1000).format("YYYY-MM-DD") === date)
      this.activeIndex = indexInCalendar !== -1 ? indexInCalendar : null

      const ts = this.$moment(date).valueOf()
      this.selectedDayData = { date: ts }

      await this.loadDayData(date, ts)
    },

    // Hilfsmethode um Code-Duplizierung zu vermeiden
    async loadDayData(dateString, ts) {
      this.dayLoadError = ""
      const requestVersion = ++this.dayRequestVersion
      const owner = userService.getUserId()
      try {
        if (this.userService.hasRight("gf_cp_instructor")) {
          // Hinweis: Hier könnte man optimieren und erst laden, wenn der User "Abrechnung" klickt,
          // aber wir behalten die Logik bei, falls du sie brauchst.
          this.fetchGFHours(dateString)
        }

        const dayData = await api.fetchTrainingDataForDay(dateString)
        if (!this._calendarAlive || requestVersion !== this.dayRequestVersion || owner !== userService.getUserId()) return
        this.selectedDayData = { ...dayData, date: ts }
      } catch (e) {
        if (this._calendarAlive && requestVersion === this.dayRequestVersion && owner === userService.getUserId()) {
          this.dayLoadError = "Dieser Tag konnte nicht geladen werden. Bitte wähle ihn erneut aus."
          this.$toast.error("Fehler beim Laden")
        }
      } finally {
        if (this._calendarAlive && requestVersion === this.dayRequestVersion) this.loadingDayDetails = false
      }
    },

    async fetchGFHours(date) {
      this.fetchingGFHours = true
      this.GFHourInfos = { list: [], totalDuration: 0 }
      try {
        let startDate = this.$moment(date).startOf("month").unix()
        let endDate = this.$moment(date).endOf("month").unix()
        this.GFHourInfos = await api.getGFHoursForSelf(startDate, endDate)
      } catch (e) {
        console.error(e)
      } finally {
        this.fetchingGFHours = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* --- VARIABLES --- */
$primary-color: var(--v-primary-base, #00963f);
$glass-bg: rgba(255, 255, 255, 0.05);
$glass-border: rgba(255, 255, 255, 0.08);

.my-training-container {
  color: white;
}

.italic-header {
  font-style: italic;
  letter-spacing: -0.5px;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
}

/* Glass Card Logic */
.glass-card {
  background: $glass-bg !important;
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border: 1px solid $glass-border;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
}

.glass-inner-card {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.min-width-0 {
  min-width: 0;
}

.activity-card-shell {
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 9px 24px rgba(0, 0, 0, 0.18);
}

.watch-source-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 7px;
  border-radius: 999px;
  border: 1px solid rgba(0, 150, 63, 0.28);
  background: rgba(0, 150, 63, 0.12);
  color: #8fffc6;
  font-size: 0.56rem;
  font-weight: 900;
  letter-spacing: 0.8px;
  white-space: nowrap;
}

.watch-home-health {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.watch-home-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  padding-top: 10px;
}

.watch-home-metric {
  min-width: 0;
  padding: 8px 7px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.24);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.watch-home-metric > span {
  display: block;
  margin-bottom: 2px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.55rem;
  font-weight: 800;
  letter-spacing: 0.65px;
}

.watch-home-metric strong {
  color: #fff;
  font-size: 0.9rem;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.watch-home-metric small {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.54rem;
  font-weight: 700;
}

.watch-home-chart {
  position: relative;
  display: block;
  width: 100%;
  height: 58px;
  padding: 2px 7px;
  overflow: hidden;
  border-radius: 10px;
  background:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    rgba(0, 0, 0, 0.2);
  background-size: 100% 18px;
  border: 1px solid rgba(255, 83, 109, 0.14);
}

.watch-home-chart-button {
  appearance: none;
  color: inherit;
  text-align: inherit;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease;
}

.watch-home-chart-button:hover {
  border-color: rgba(255, 83, 109, 0.38);
  box-shadow: 0 8px 24px rgba(255, 83, 109, 0.08);
}

.watch-home-chart-button:active {
  transform: scale(0.985);
}

.watch-home-expand-pill {
  position: absolute;
  top: 5px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.86);
  background: rgba(8, 10, 10, 0.76);
  font-size: 0.5rem;
  font-weight: 800;
  letter-spacing: 0.25px;
  pointer-events: none;
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
}

.watch-home-chart svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.watch-home-health-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.64rem;
  font-weight: 700;
}

.watch-home-health-footer span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.watch-home-detail-button {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 7px;
  color: #fff;
  font-size: 0.58rem;
  font-weight: 900;
  border: 1px solid rgba(143, 255, 198, 0.2);
  border-radius: 999px;
  background: rgba(0, 150, 63, 0.13);
}

.custom-skeleton {
  background: rgba(255, 255, 255, 0.05) !important;
}
::v-deep .v-skeleton-loader__image {
  background: rgba(255, 255, 255, 0.05) !important;
}

/* Kalender Grid Styles */
.day-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
}

.day-name {
  font-size: 0.7rem;
  color: #888;
  margin-bottom: 6px;
  font-weight: 600;
}

.day-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  font-weight: bold;
  color: white;
}

/* Interaktive States */
.day-active .day-circle {
  background: rgba(0, 150, 63, 0.2);
  border-color: $primary-color;
  color: $primary-color;
  box-shadow: 0 0 10px rgba(0, 150, 63, 0.3);
}

.day-today .day-name {
  color: $primary-color;
  font-weight: 900;
}

.day-selected .day-circle {
  background: white !important;
  color: black !important;
  transform: scale(1.15);
  border-color: white !important;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
}

/* Detail Box Styles */
.rest-day-box {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), transparent);
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

.success-gradient-soft {
  background: linear-gradient(90deg, rgba(0, 150, 63, 0.2) 0%, transparent 100%);
}

.border-bottom-light {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.emoji-bounce {
  animation: bounce 2s infinite;
  font-size: 3rem;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(5px);
  }
  60% {
    transform: translateY(3px);
  }
}

/* --- RECHARGE ZONE STYLES --- */

.recharge-card {
  /* Dunkler, tiefer Hintergrund für Kontrast */
  background: linear-gradient(180deg, rgba(20, 20, 20, 0.6) 0%, rgba(0, 0, 0, 0.4) 100%);
  border: 1px solid rgba(0, 150, 63, 0.3); /* Zartes Grün im Rahmen */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
}

/* Der schwebende grüne Nebel im Hintergrund */
.ambient-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, rgba(0, 150, 63, 0.4) 0%, transparent 70%);
  transform: translate(-50%, -50%);
  filter: blur(40px);
  animation: breathe 4s ease-in-out infinite;
  z-index: 1;
}

/* Icon Wrapper */
.icon-wrapper {
  position: relative;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 2;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* Pulsierender Ring um das Icon */
.pulse-ring {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  border: 2px solid #00963f;
  animation: ripple 2s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}

/* Pillen-Style für den Status */
.status-pill {
  background: rgba(0, 150, 63, 0.1);
  border: 1px solid rgba(0, 150, 63, 0.2);
  backdrop-filter: blur(4px);
}

/* Utilities */
.z-index-2 {
  position: relative;
  z-index: 2;
}

.tracking-wider {
  letter-spacing: 2px !important;
}

/* ANIMATIONEN */

@keyframes breathe {
  0%,
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1.3);
  }
}

@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.blink {
  animation: blinker 1.5s linear infinite;
}

@keyframes blinker {
  50% {
    opacity: 0.3;
  }
}
.relative {
  position: relative;
}

.dialog-glow {
  top: -20%;
  right: -20%;
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(0, 150, 63, 0.3) 0%, transparent 70%);
}

/* Picker Deep Styling für Glass-Look */
.glass-picker {
  background-color: transparent !important;
  box-shadow: none !important;
}

/* WICHTIG: Überschreibt die internen Vuetify Styles für Transparenz */
::v-deep .v-picker__body {
  background-color: transparent !important;
  width: 100% !important;
}

/* Header Text (Monat/Jahr) */
::v-deep .v-date-picker-header__value button {
  color: white !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 1px;
}

::v-deep .v-date-picker-header button .v-icon {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Wochentage (Mo, Di...) */
::v-deep .v-date-picker-table th {
  color: rgba(255, 255, 255, 0.5) !important;
  font-weight: 600;
}

/* Die Tage selbst */
::v-deep .v-date-picker-table .v-btn {
  color: white !important;
  font-weight: 500;
}

/* Deaktivierte Tage (Zukunft) */
::v-deep .v-theme--light.v-btn.v-btn--disabled {
  color: rgba(255, 255, 255, 0.2) !important;
}

/* Der Event-Punkt unter dem Datum */
::v-deep .v-date-picker-table .v-date-picker-table__events {
  bottom: 4px;
}

/* Selektierter Tag Style Anpassung */
::v-deep .v-date-picker-table .v-btn.v-btn--active {
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.6);
  font-weight: 900;
  color: white !important;
}

/* Keep the calendar quiet: the booking and measured values carry the hierarchy. */
.calendar-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.calendar-heading > div { display: flex; flex-direction: column; gap: 3px; }
.calendar-heading span { color: #acb8b0; font-size: .65rem; font-weight: 700; letter-spacing: .09em; }
.calendar-heading strong { font-size: 1.1rem; line-height: 1.3; }
.next-course { display: flex; width: 100%; align-items: center; gap: 11px; margin-top: 16px; padding: 13px 12px; text-align: left; color: white; border: 1px solid rgba(0,150,63,.3); border-radius: 16px; background: linear-gradient(115deg, rgba(0,150,63,.12), rgba(255,255,255,.025)); }
.next-course-icon { display: grid; width: 38px; height: 38px; flex: 0 0 38px; place-items: center; border-radius: 12px; background: rgba(0,150,63,.2); }
.next-course-copy { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 3px; }
.next-course-copy small { font-size: .625rem; font-weight: 700; letter-spacing: .05em; color: #bccbc2; }
.next-course-copy strong { font-size: .9375rem; line-height: 1.3; overflow-wrap: anywhere; }
.next-course-copy > span { color: #b5c0b9; font-size: .75rem; line-height: 1.4; }
.activity-title { font-size: .9375rem; font-weight: 700; color: white; line-height: 1.35; overflow-wrap: anywhere; }
.activity-subtitle { margin-top: 2px; font-size: .75rem; line-height: 1.4; color: #acb8b0; }
.activity-status { margin-top: 4px; color: #d1dbd5; font-size: .6875rem; line-height: 1.4; }
.watch-source-pill { width: 32px; height: 32px; flex: 0 0 32px; justify-content: center; padding: 0; color: white; border-color: rgba(0,150,63,.25); background: rgba(0,150,63,.12); }
.watch-home-metric { padding: 10px 7px; border: 0; background: rgba(0,0,0,.18); }
.watch-home-metric > span { font-size: .625rem; color: #b5c0b9; letter-spacing: .025em; line-height: 1.4; }
.watch-home-metric strong { font-size: 1.1rem; line-height: 1.35; }
.watch-home-metric small { font-size: .625rem; color: #b5c0b9; }
.watch-home-health-footer { gap: 8px; flex-wrap: wrap; font-size: .75rem; color: #b5c0b9; }
.watch-home-device { flex: 1; }
.watch-home-detail-button { min-height: 36px; padding: 6px 10px; gap: 5px; font-size: .75rem; border-color: rgba(0,150,63,.3); background: rgba(0,150,63,.12); }
.watch-home-expand-pill { font-size: .625rem; padding: 4px 7px; }
.watch-home-note { margin: 10px 0 0; color: #b5c0b9; font-size: .75rem; line-height: 1.5; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; }
.next-course:focus-visible, .watch-home-detail-button:focus-visible, .watch-home-chart-button:focus-visible { outline: 2px solid white; outline-offset: 3px; }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
</style>
